import { useNavigate, useRouterState } from '@tanstack/react-router';
import { Building2, Clock, Handshake, Loader2, MonitorPlay, Plug2 } from 'lucide-react';
import { useCallback, useEffect, useEffectEvent, useMemo, useState } from 'react';

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@shigo-ui/ui';

import { useNetworkFeatures } from '../../auth/useNetworkFeatures';
import { useShigoDashboardContext } from '../../auth/useShigoDashboardContext';
import { useShigoPermissions } from '../../auth/useShigoPermissions';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import { dashboardT } from '../../../lib/messages';
import {
  closeCommandPalette,
  openCommandPalette,
  useCommandPaletteOpen,
} from '../commandPaletteStore';
import { SearchEntityKind, type SearchResult } from '../model';
import { pushRecentSearch, readRecentSearches } from '../recentSearches';
import { searchableEntityKinds } from '../searchAccess';
import { useCommandPaletteAvailability } from '../useCommandPaletteAvailability';
import { useGlobalSearch } from '../useGlobalSearch';

const SEARCH_DEBOUNCE_MS = 250;
const GROUP_ICONS: Record<SearchEntityKind, typeof Building2> = {
  [SearchEntityKind.Publisher]: Building2,
  [SearchEntityKind.Placement]: MonitorPlay,
  [SearchEntityKind.DemandPartner]: Handshake,
  [SearchEntityKind.Integration]: Plug2,
};
const GROUP_LABEL_KEYS: Record<SearchEntityKind, Parameters<typeof dashboardT>[0]> = {
  [SearchEntityKind.Publisher]: 'search.group.publishers',
  [SearchEntityKind.Placement]: 'search.group.placements',
  [SearchEntityKind.DemandPartner]: 'search.group.demandPartners',
  [SearchEntityKind.Integration]: 'search.group.integrations',
};

function isEditableTarget(target: EventTarget | null) {
  return (
    target instanceof HTMLElement &&
    (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
  );
}

export function CommandPalette() {
  const isAvailable = useCommandPaletteAvailability();
  const isOpen = useCommandPaletteOpen();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const dashboardContext = useShigoDashboardContext(pathname);
  const { permissions, can } = useShigoPermissions();
  const { features, isFeatureAllowed } = useNetworkFeatures();
  const allowedKinds = useMemo(
    () => searchableEntityKinds({ dashboardContext, can, isFeatureAllowed }),
    // Hook callbacks are recreated; their underlying data is the stable dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dashboardContext, permissions, features],
  );
  const [committedTerm, setCommittedTerm] = useState('');
  const search = useDebouncedValue(committedTerm, setCommittedTerm, SEARCH_DEBOUNCE_MS);
  const { setDraft } = search;
  const { groups, isLoading, showIdHint, hasResults, idMatch } = useGlobalSearch(
    isOpen && isAvailable ? committedTerm : '',
    allowedKinds,
  );

  function resetSearchInput() {
    search.setDraft('');
    setCommittedTerm('');
  }

  function handleOpenChange(nextOpen: boolean) {
    if (nextOpen) openCommandPalette();
    else {
      closeCommandPalette();
      resetSearchInput();
    }
  }

  const onShortcutKeyDown = useEffectEvent((event: KeyboardEvent) => {
    if (event.key.toLowerCase() !== 'k' || !(event.metaKey || event.ctrlKey)) return;
    if (!isOpen && isEditableTarget(event.target)) return;
    event.preventDefault();
    handleOpenChange(!isOpen);
  });

  useEffect(() => {
    if (!isAvailable) return;
    function handleKeyDown(event: KeyboardEvent) {
      onShortcutKeyDown(event);
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isAvailable]);

  useEffect(
    () => () => {
      closeCommandPalette();
    },
    [],
  );

  const handleSelect = useCallback(
    (result: SearchResult) => {
      pushRecentSearch(result);
      closeCommandPalette();
      setDraft('');
      setCommittedTerm('');
      void navigate({ to: result.to as never, params: result.params as never });
    },
    [navigate, setDraft],
  );

  useEffect(() => {
    if (isOpen && isAvailable && idMatch && search.draft.trim() === committedTerm.trim()) {
      // A completed asynchronous lookup uses the same navigation and input reset as selection.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      handleSelect(idMatch);
    }
  }, [isOpen, isAvailable, idMatch, search.draft, committedTerm, handleSelect]);

  if (!isAvailable) return null;
  const trimmedTerm = committedTerm.trim();
  const recentResults = trimmedTerm
    ? []
    : readRecentSearches().filter((result) => allowedKinds.has(result.kind));

  return (
    <CommandDialog
      description={dashboardT('search.dialog.description')}
      onOpenChange={handleOpenChange}
      open={isOpen}
      shouldFilter={false}
      title={dashboardT('search.dialog.title')}
    >
      <CommandInput
        onValueChange={search.setDraft}
        placeholder={dashboardT('search.placeholder')}
        value={search.draft}
      />
      {showIdHint ? (
        <p className="text-muted-foreground border-b px-3 py-2 text-xs">
          {dashboardT('search.hint.id')}
        </p>
      ) : null}
      <CommandList>
        {isLoading ? (
          <div className="text-muted-foreground flex items-center gap-2 px-4 py-3 text-sm">
            <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />
            {dashboardT('search.searching')}
          </div>
        ) : null}
        {!trimmedTerm && recentResults.length > 0 ? (
          <CommandGroup heading={dashboardT('search.recent')}>
            {recentResults.map((result) => (
              <SearchResultItem
                icon={Clock}
                key={`recent-${result.kind}-${result.id}`}
                onSelect={handleSelect}
                result={result}
              />
            ))}
          </CommandGroup>
        ) : null}
        {trimmedTerm && !isLoading && !hasResults ? (
          <CommandEmpty>{dashboardT('search.empty')}</CommandEmpty>
        ) : null}
        {trimmedTerm
          ? groups.map((group) =>
              group.results.length > 0 ? (
                <CommandGroup heading={dashboardT(GROUP_LABEL_KEYS[group.kind])} key={group.kind}>
                  {group.results.map((result) => (
                    <SearchResultItem
                      icon={GROUP_ICONS[group.kind]}
                      key={`${result.kind}-${result.id}`}
                      onSelect={handleSelect}
                      result={result}
                    />
                  ))}
                </CommandGroup>
              ) : null,
            )
          : null}
      </CommandList>
    </CommandDialog>
  );
}

function SearchResultItem({
  icon: Icon,
  onSelect,
  result,
}: {
  icon: typeof Building2;
  onSelect: (result: SearchResult) => void;
  result: SearchResult;
}) {
  return (
    <CommandItem
      onSelect={() => {
        onSelect(result);
      }}
      value={`${result.kind}-${result.id}-${result.label}`}
    >
      <Icon aria-hidden="true" className="h-4 w-4 shrink-0" />
      <span className="min-w-0 flex-1 truncate">{result.label}</span>
      {result.sublabel ? (
        <span className="text-muted-foreground ml-2 shrink-0 truncate text-xs">
          {result.sublabel}
        </span>
      ) : null}
    </CommandItem>
  );
}
